import { useTranslation } from "react-i18next";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useLocalizedDate } from "@/components/site/shared";
import { ARTICLES } from "@/data/site";

export default function ArticleDetail() {
  const { t } = useTranslation();
  const fmtDate = useLocalizedDate();
  const { articleId } = useParams();
  const article = ARTICLES.find((a) => a.id === articleId);

  if (!article) return <Navigate to="/insights" replace />;

  const related = ARTICLES.filter((a) => a.id !== articleId);

  return (
    <div data-testid={`article-detail-${articleId}`}>
      <section className="border-b bg-secondary/50">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
          <Link to="/insights" className="inline-flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-primary transition-colors" data-testid="back-to-insights">
            <ArrowLeft className="h-4 w-4 rtl:rotate-180" />
            {t("insights.back")}
          </Link>
          <div className="mt-8 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
            <span className="bg-primary text-primary-foreground px-2 py-0.5 font-semibold uppercase tracking-wider">{t(`insights.tags.${article.tag}`)}</span>
            <span>{fmtDate(article.date)}</span>
            <span>· {article.minutes} {t("insights.minRead")}</span>
            <span>· {t("insights.author")}</span>
          </div>
          <h1 className="mt-5 font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight leading-tight">{t(`articles.${articleId}.title`)}</h1>
        </div>
      </section>

      <section className="py-12 lg:py-16">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <div className="aspect-video overflow-hidden border">
            <img src={article.image} alt="" className="h-full w-full object-cover" />
          </div>
          <div className="mt-10 space-y-6 text-base md:text-lg leading-relaxed text-foreground/85">
            <p>{t(`articles.${articleId}.p1`)}</p>
            <p>{t(`articles.${articleId}.p2`)}</p>
            <p>{t(`articles.${articleId}.p3`)}</p>
          </div>
        </div>
      </section>

      <section className="py-16 bg-secondary/50 border-t" data-testid="related-articles">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <h2 className="font-heading text-2xl font-extrabold tracking-tight">{t("insights.related")}</h2>
          <div className="mt-8 grid md:grid-cols-2 gap-6">
            {related.map((a) => (
              <Link to={`/insights/${a.id}`} key={a.id} className="group border bg-card flex overflow-hidden" data-testid={`related-article-${a.id}`}>
                <div className="w-1/3 shrink-0 overflow-hidden">
                  <img src={a.image} alt="" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                </div>
                <div className="p-5">
                  <span className="text-xs text-muted-foreground">{fmtDate(a.date)}</span>
                  <h3 className="mt-1.5 font-heading text-base font-bold leading-snug group-hover:text-primary transition-colors">{t(`articles.${a.id}.title`)}</h3>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
